import { useTranslations } from "next-intl";
import { css, cx } from "styled-system/css";
import { grid, stack } from "styled-system/patterns";

import { RadioTabGroup } from "@/components/radio-tab-group";
import { useIsNetworkDown } from "@/contexts/health";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { TradingModes, useUserPreferences } from "@/hooks/use-user-preferences";

export function SelectTradingMode() {
  const t = useTranslations("advancedSettings");
  const { writeDexSdk } = useWriteDexSdk();
  const isNetworkDown = useIsNetworkDown();
  const { tradingMode, updateTradingMode } = useUserPreferences();

  return (
    <div className={stack({ gap: "8" })}>
      <p className={css({ textStyle: "body.md" })}>{t("orderFormLayout")}</p>
      <RadioTabGroup
        disabled={!writeDexSdk || isNetworkDown}
        value={tradingMode}
        onChange={updateTradingMode}
        variant="layoutSelector"
        options={[
          {
            value: TradingModes.BASIC,
            label: (
              <ControlContainer className={tabsLayoutContainerStyles}>
                <div className={cx(positiveBlockStyles, css({ gridRow: "1", gridColumn: "1" }))} />
                <div className={cx(negativeBlockStyles, css({ gridRow: "1", gridColumn: "2" }))} />
                <div
                  className={cx(
                    neutralBlockStyles,
                    css({ gridRow: "2 / 4", gridColumn: "1 / -1" }),
                  )}
                />
                <div
                  className={cx(primaryBlockStyles, css({ gridRow: "4", gridColumn: "1 / -1" }))}
                />
              </ControlContainer>
            ),
          },
          {
            value: TradingModes.ADVANCED,
            label: (
              <ControlContainer className={buttonsLayoutContainerStyles}>
                <div
                  className={cx(
                    neutralBlockStyles,
                    css({ gridRow: "1 / 4", gridColumn: "1 / -1" }),
                  )}
                />
                <div className={cx(positiveBlockStyles, css({ gridRow: "4", gridColumn: "1" }))} />
                <div className={cx(negativeBlockStyles, css({ gridRow: "4", gridColumn: "2" }))} />
              </ControlContainer>
            ),
          },
        ]}
      />
    </div>
  );
}

interface ControlContainerProps
  extends React.PropsWithChildren, React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  ref?: React.RefObject<HTMLDivElement>;
}

function ControlContainer({ children, className, ...props }: ControlContainerProps) {
  return (
    <div className={cx(controlStyles, className)} {...props}>
      {children}
    </div>
  );
}

const controlStyles = stack({
  h: "96",
  w: "96",
  p: "4",
});

const tabsLayoutContainerStyles = grid({
  gridTemplateColumns: "1fr 1fr",
  gridTemplateRows: "1fr 1fr 1fr 1fr",
  gridGap: "4",
});

const buttonsLayoutContainerStyles = grid({
  gridTemplateColumns: "1fr 1fr",
  gridTemplateRows: "1fr 1fr 1fr 1fr",
  gridGap: "4",
});

const neutralBlockStyles = css({
  rounded: "200",
  bg: "tradingModeLayout.neutral",
});

const positiveBlockStyles = css({
  rounded: "200",
  bg: "tradingModeLayout.positive",
});

const negativeBlockStyles = css({
  rounded: "200",
  bg: "tradingModeLayout.negative",
});

const primaryBlockStyles = css({
  rounded: "200",
  bg: "tradingModeLayout.primary",
});
